// Payment-first checkout creates the account at charge time, so the FE needs a
// throwaway credential to send in the checkout request and sign in with
// immediately afterward. The user replaces it on the set-password screen (U5).
//
// Requirements:
//  • crypto-backed (never Math.random) — this is a real account credential
//  • satisfies Cognito's complexity policy (upper, lower, digit, symbol)
//  • no whitespace (omg-api rejects PASSWORD_HAS_WHITESPACE)
//  • unique per call
//
// Modulo bias from `% pool.length` is irrelevant here: the value is a
// machine-generated throwaway, never user-chosen, and is rotated out the moment
// the user sets a real password.

const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWER = "abcdefghijklmnopqrstuvwxyz";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+";
const ALL = UPPER + LOWER + DIGITS + SYMBOLS;
const LENGTH = 24;

function randomIndex(max: number): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] % max;
}

export function generateDummyPassword(): string {
  // Seed one char from each required class so the result always satisfies the
  // complexity policy regardless of the random draw, then fill and shuffle.
  const chars = [
    UPPER[randomIndex(UPPER.length)],
    LOWER[randomIndex(LOWER.length)],
    DIGITS[randomIndex(DIGITS.length)],
    SYMBOLS[randomIndex(SYMBOLS.length)],
  ];
  while (chars.length < LENGTH) {
    chars.push(ALL[randomIndex(ALL.length)]);
  }
  // Fisher–Yates with crypto indices so the seeded chars aren't pinned to the
  // first four positions.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
